/**
 * One asset at its true size, inside a real modal boundary.
 *
 * Two defects are fixed here and both are structural.
 *
 * The first is containment: `aria-modal` without a focus trap is a false
 * promise, so this dialog actually traps Tab, marks the page behind it `inert`
 * (with an `aria-hidden` fallback), locks body scroll, and restores focus to
 * the opener. The portal is not decoration either — every plate in the system
 * is `overflow: hidden`, which is why an in-place lightbox always ends up
 * clipped inside a card.
 *
 * The second is sizing: the frame is sized to the IMAGE, never to the viewport,
 * and never upscaled past 1:1. A 100×100 cover opens at 100×100 on a black
 * field; a 120px proxy thumbnail does not float inside a shadowed 1200×700
 * rounded rectangle pretending to be a photograph. When the dimensions are not
 * known up front the primitive measures the decoded bitmap before it paints a
 * frame.
 *
 * The frame is an IMAGE-ONLY wrapper, and that is the whole of the third defect.
 * The asset's size used to be written onto the `<figure>` — a flex column
 * holding the picture AND the caption — so the caption's height and the gap
 * above it came out of the picture's share: a 92×92 asset produced a correct
 * 92×92 figure and a 22×22 image, a 0.24× downscale, which is the exact mirror
 * of the upscale this file already refused. The figure now has no block size at
 * all; the picture's own box carries it, and the caption sits under a frame
 * that is 1:1.
 */
"use client";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import { createPortal } from "react-dom";
import { IconChevronLeft, IconChevronRight, IconX } from "@tabler/icons-react";
import { Cbutton } from "@/components/ui/Cbutton";
import { Cimage, CIMAGE_ANIMATION, CIMAGE_OBJECT_FIT } from "@/components/ui/Cimage";
import { ButtonPreset } from "@/constants/componentPresets";
import {
  CRC_R_PILL,
  CRC_R_TILE,
  CRC_TEXT,
  CRC_TOUCH_TARGET,
} from "./cresultcard.tokens";

const CLOSE_LABEL = "Close";
const PREVIOUS_LABEL = "Previous";
const NEXT_LABEL = "Next";
const DIALOG_LABEL_PREFIX = "Image viewer: ";
const BODY_OVERFLOW_HIDDEN = "hidden";
/**
 * `CRC_TOUCH_TARGET`, not a size of its own. These three were 38×38 — under
 * SPEC §7.4's floor at every viewport, in a dialog whose only controls they are.
 * They are absolutely positioned on a full-screen field with 22px of clear
 * ground around each, so the floor costs no layout here: nothing reflows and
 * nothing else moves.
 */
const VIEWER_BUTTON_SIZE = CRC_TOUCH_TARGET;
const VIEWER_BUTTON_RADIUS = VIEWER_BUTTON_SIZE / 2;
const VIEWER_EDGE_INSET = 22;
/** The viewport is the ceiling, never the target. */
const FRAME_MAX_WIDTH = "88vw";
const FRAME_MAX_HEIGHT = "80dvh";
const FOCUSABLE_SELECTOR = "[data-crc-viewer-control]";
/** The top layer answers a key before anything it is painted over does. */
const KEY_CAPTURE = true;
const INERT_ATTRIBUTE = "inert";
const ARIA_HIDDEN_ATTRIBUTE = "aria-hidden";
const ATTRIBUTE_PRESENT = "";
const ARIA_HIDDEN_TRUE = "true";

/** `data-crc-viewer-control` is the trap's own tab ring, not the kit's. */
const CLOSE_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/Lightbox/CloseTrigger",
  "data-crc-control": true,
  "data-crc-viewer-control": true,
};
const PREVIOUS_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/Lightbox/PreviousTrigger",
  "data-crc-control": true,
  "data-crc-viewer-control": true,
};
const NEXT_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/Lightbox/NextTrigger",
  "data-crc-control": true,
  "data-crc-viewer-control": true,
};

const VIEWER_BUTTON_FILL: CSSProperties = { width: "100%", height: "100%" };

const VIEWER_BUTTON_STYLE: CSSProperties = {
  position: "absolute",
  zIndex: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: VIEWER_BUTTON_SIZE,
  height: VIEWER_BUTTON_SIZE,
  color: CRC_TEXT,
};

enum LightboxKey {
  Escape = "Escape",
  ArrowLeft = "ArrowLeft",
  ArrowRight = "ArrowRight",
  Tab = "Tab",
}

export interface CresultcardAsset {
  readonly src: string;
  readonly alt: string;
  readonly intrinsicWidth?: number;
  readonly intrinsicHeight?: number;
}

interface MeasuredSize {
  readonly width: number;
  readonly height: number;
}

interface CresultcardLightboxProps {
  readonly assets: readonly CresultcardAsset[];
  readonly index: number;
  readonly onClose: () => void;
  readonly onIndexChange: (index: number) => void;
  /**
   * `n / m of <set label>` when a set opted into set-scoped paging. Item-scoped
   * viewers say nothing: stepping from one business's photo to another
   * business's photo under a caption that does not change is a comprehension
   * failure, so set scope has to announce itself.
   */
  readonly setLabel?: string;
}

function CresultcardLightbox({
  assets,
  index,
  onClose,
  onIndexChange,
  setLabel,
}: CresultcardLightboxProps): ReactElement | null {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const [measured, setMeasured] = useState<MeasuredSize | null>(null);
  const total = assets.length;
  const current = assets[index];

  const step = useCallback(
    (delta: number): void => {
      onIndexChange((index + delta + total) % total);
    },
    [index, onIndexChange, total],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === LightboxKey.Escape) {
        // Marked handled, and handled FIRST. The viewer is opened from inside
        // a set's drill pane, whose own Escape closes the drill — one press
        // must dismiss the top layer only, so this runs in the capture phase
        // and the pane's handler reads `defaultPrevented` and stands down.
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === LightboxKey.ArrowRight) {
        event.preventDefault();
        step(1);
        return;
      }
      if (event.key === LightboxKey.ArrowLeft) {
        event.preventDefault();
        step(-1);
        return;
      }
      if (event.key !== LightboxKey.Tab) {
        return;
      }
      // The trap. `aria-modal` alone lets Tab walk straight out of the dialog
      // and into the page it claims to have made inert.
      const dialog = dialogRef.current;
      if (dialog == null) {
        return;
      }
      const stops = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (stops.length === 0) {
        return;
      }
      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey, KEY_CAPTURE);
    return () => window.removeEventListener("keydown", onKey, KEY_CAPTURE);
  }, [onClose, step]);

  useEffect(() => {
    const dialog = dialogRef.current;
    const opener = openerRef;
    opener.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = BODY_OVERFLOW_HIDDEN;
    // Everything the portal is a sibling of goes inert. Marking one app-shell
    // id would be shorter and would silently do nothing the day the shell's id
    // changes, which is the failure mode a false `aria-modal` already has.
    const backdrop = Array.from(document.body.children).filter(
      (child): child is HTMLElement =>
        child instanceof HTMLElement && child !== dialog,
    );
    backdrop.forEach((element) => {
      element.setAttribute(INERT_ATTRIBUTE, ATTRIBUTE_PRESENT);
      element.setAttribute(ARIA_HIDDEN_ATTRIBUTE, ARIA_HIDDEN_TRUE);
    });
    dialog?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      backdrop.forEach((element) => {
        element.removeAttribute(INERT_ATTRIBUTE);
        element.removeAttribute(ARIA_HIDDEN_ATTRIBUTE);
      });
      opener.current?.focus();
    };
  }, []);

  const handleMeasure = useCallback(
    (event: SyntheticEvent<HTMLImageElement>): void => {
      const image = event.currentTarget;
      setMeasured({
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    },
    [],
  );

  /**
   * The picture's own inline size, shared by the frame and the figure that
   * captions it. Until the bitmap has been decoded there is no width to state,
   * and the pair fall back to the viewport ceiling together.
   */
  const frameInlineSize = useMemo<CSSProperties>((): CSSProperties => {
    const width = current?.intrinsicWidth ?? measured?.width;
    return {
      width:
        width == null ? undefined : `min(${FRAME_MAX_WIDTH}, ${String(width)}px)`,
    };
  }, [current, measured]);

  const frameSize = useMemo<CSSProperties>((): CSSProperties => {
    const height = current?.intrinsicHeight ?? measured?.height;
    return {
      ...frameInlineSize,
      height:
        height == null
          ? undefined
          : `min(${FRAME_MAX_HEIGHT}, ${String(height)}px)`,
      maxWidth: FRAME_MAX_WIDTH,
      maxHeight: FRAME_MAX_HEIGHT,
    };
  }, [current, frameInlineSize, measured]);

  if (typeof document === "undefined" || current == null) {
    return null;
  }

  return createPortal(
    <div
      aria-label={`${DIALOG_LABEL_PREFIX}${current.alt}`}
      aria-modal
      className="cresultcard cresultcard__lightbox"
      data-el="Cresultcard/Lightbox/Backdrop"
      onClick={onClose}
      ref={dialogRef}
      role="dialog"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingBlock: "6vh",
        paddingInline: "6vw",
        backgroundColor: "rgba(2,5,4,0.86)",
      }}
      tabIndex={-1}
    >
      <Cbutton
        ariaLabel={CLOSE_LABEL}
        containerStyle={VIEWER_BUTTON_FILL}
        disableClickMotion
        onClick={onClose}
        padding={0}
        preset={ButtonPreset.Ghost}
        radius={VIEWER_BUTTON_RADIUS}
        rootAttributes={CLOSE_ROOT_ATTRIBUTES}
        style={{
          ...VIEWER_BUTTON_STYLE,
          insetBlockStart: VIEWER_EDGE_INSET,
          insetInlineEnd: VIEWER_EDGE_INSET,
        }}
      >
        <IconX size={16} stroke={1.4} />
      </Cbutton>

      {total > 1 && (
        <Cbutton
          ariaLabel={PREVIOUS_LABEL}
          containerStyle={VIEWER_BUTTON_FILL}
          disableClickMotion
          onClick={(event) => {
            event?.stopPropagation();
            step(-1);
          }}
          padding={0}
          preset={ButtonPreset.Ghost}
          radius={VIEWER_BUTTON_RADIUS}
          rootAttributes={PREVIOUS_ROOT_ATTRIBUTES}
          style={{
            ...VIEWER_BUTTON_STYLE,
            insetBlockStart: "50%",
            insetInlineStart: VIEWER_EDGE_INSET,
          }}
        >
          <IconChevronLeft size={18} stroke={1.4} />
        </Cbutton>
      )}

      <figure
        className="cresultcard__lightbox-figure"
        data-el="Cresultcard/Lightbox/Panel"
        data-crc-viewer-control
        onClick={(event) => event.stopPropagation()}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
          // The figure takes the picture's inline SIZE, not just its ceiling,
          // and NO block size of its own. A ceiling alone did not contain the
          // caption: a flex column is as wide as its widest item, so a 100px
          // frame under a 271.86px caption produced a 271.86px figure with the
          // caption hanging 85.9px past the picture on each side. With the
          // frame's own width on the figure the caption has to wrap inside it,
          // which is what the frame was for.
          ...frameInlineSize,
          maxWidth: FRAME_MAX_WIDTH,
          margin: 0,
        }}
        tabIndex={0}
      >
        <span
          style={{
            ...frameSize,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 0,
          }}
        >
          <Cimage
            alt={current.alt}
            animation={CIMAGE_ANIMATION.None}
            key={current.src}
            objectFit={CIMAGE_OBJECT_FIT.Contain}
            onLoad={handleMeasure}
            radius={CRC_R_TILE}
            src={current.src}
            style={{ width: "auto", height: "auto", maxWidth: "100%", maxHeight: "100%" }}
          />
        </span>
        <figcaption
          style={{
            fontSize: 11,
            fontWeight: 540,
            textAlign: "center",
            // A narrow frame is a narrow measure, and an unbroken alt string is
            // the one caption a wrap opportunity cannot contain on its own.
            overflowWrap: "anywhere",
            color: CRC_TEXT,
          }}
        >
          {current.alt}
        </figcaption>
      </figure>

      {total > 1 && (
        <Cbutton
          ariaLabel={NEXT_LABEL}
          containerStyle={VIEWER_BUTTON_FILL}
          disableClickMotion
          onClick={(event) => {
            event?.stopPropagation();
            step(1);
          }}
          padding={0}
          preset={ButtonPreset.Ghost}
          radius={VIEWER_BUTTON_RADIUS}
          rootAttributes={NEXT_ROOT_ATTRIBUTES}
          style={{
            ...VIEWER_BUTTON_STYLE,
            insetBlockStart: "50%",
            insetInlineEnd: VIEWER_EDGE_INSET,
          }}
        >
          <IconChevronRight size={18} stroke={1.4} />
        </Cbutton>
      )}

      {total > 1 && (
        <span
          data-el="Cresultcard/Lightbox/Status"
          style={{
            position: "absolute",
            insetBlockEnd: 26,
            paddingBlock: 5,
            paddingInline: 12,
            borderRadius: CRC_R_PILL,
            fontSize: 10.5,
            fontWeight: 600,
            color: CRC_TEXT,
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
        >
          {`${String(index + 1)} / ${String(total)}${setLabel == null ? "" : ` of ${setLabel}`}`}
        </span>
      )}
    </div>,
    document.body,
  );
}

export interface CresultcardLightboxHandle {
  /** Opens the viewer at one asset. */
  readonly open: (index: number) => void;
  /** Render this wherever the composition sits; it portals to the body. */
  readonly viewer: ReactNode;
}

interface UseCresultcardLightboxInput {
  readonly assets: readonly CresultcardAsset[];
  readonly setLabel?: string;
}

/**
 * Item-scoped by default. A set opts into set-scoped paging by handing the
 * hook the full asset list and its own label, and the pager then says which
 * set the reader is inside.
 */
export function useCresultcardLightbox({
  assets,
  setLabel,
}: UseCresultcardLightboxInput): CresultcardLightboxHandle {
  const [index, setIndex] = useState<number | null>(null);
  const open = useCallback((next: number): void => setIndex(next), []);
  const close = useCallback((): void => setIndex(null), []);

  const viewer =
    index === null ? null : (
      <CresultcardLightbox
        assets={assets}
        index={index}
        onClose={close}
        onIndexChange={setIndex}
        setLabel={setLabel}
      />
    );

  return { open, viewer };
}
